import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const AUTH_STORAGE_KEY = 'constadmin.auth';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
}

const storedAuth = (() => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
})();

if (storedAuth?.token) {
  setAuthToken(storedAuth.token);
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      setAuthToken(null);
      const path = window.location.pathname;
      if (
        !path.startsWith('/login')
        && !path.startsWith('/invite')
        && !path.startsWith('/forgot-password')
        && !path.startsWith('/reset-password')
      ) {
        window.location.assign('/login');
      }
    }

    return Promise.reject(error);
  }
);

export const getCompanies = () => api.get('/api/companies');
export const getCompany = (id) => api.get(`/api/companies/${id}`);
export const createCompany = (data) => api.post('/api/companies', data);
export const updateCompany = (id, data) => api.put(`/api/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/api/companies/${id}`);

export const getProjects = ({ sort, order } = {}) => {
  const params = new URLSearchParams();
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  return api.get(`/api/projects${params.toString() ? `?${params.toString()}` : ''}`);
};
export const getProject = (id) => api.get(`/api/projects/${id}`);
export const createProject = (data) => api.post('/api/projects', data);
export const updateProject = (id, data) => api.put(`/api/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/api/projects/${id}`);

// Personnel endpoints
export const getPersonnel = (config) => api.get('/api/personnel', config);
export const getAvailablePersonnel = (date) => api.get('/api/personnel/available', { params: { date } });
export const getPerson = (id) => api.get(`/api/personnel/${id}`);
export const createPerson = (data) => api.post('/api/personnel', data);
export const updatePerson = (id, data) => api.put(`/api/personnel/${id}`, data);
export const deletePerson = (id) => api.delete(`/api/personnel/${id}`);
export const getPersonnelFileCategories = () => api.get('/api/personnel-files/categories');
export const createPersonnelFileCategory = (data) => api.post('/api/personnel-files/categories', data);
export const updatePersonnelFileCategory = (categoryId, data) => api.put(`/api/personnel-files/categories/${categoryId}`, data);
export const deletePersonnelFileCategory = (categoryId) => api.delete(`/api/personnel-files/categories/${categoryId}`);
export const getPersonnelFiles = (personnelId) => api.get(`/api/personnel-files/personnel/${personnelId}/files`);
export const getPersonnelFileUploadUrl = (personnelId, data) => api.post(`/api/personnel-files/personnel/${personnelId}/presign`, data);
export const createPersonnelFile = (personnelId, data) => api.post(`/api/personnel-files/personnel/${personnelId}/files`, data);
export const updatePersonnelFile = (fileId, data) => api.patch(`/api/personnel-files/files/${fileId}`, data);
export const deletePersonnelFile = (fileId) => api.delete(`/api/personnel-files/files/${fileId}`);

export const getDueDates = () => api.get('/api/due-dates');

export const getFinanceSettings = () => api.get('/api/finance/settings');
export const updateFinanceSettings = (data) => api.patch('/api/finance/settings', data);
export const createFinanceCategory = (data) => api.post('/api/finance/categories', data);
export const updateFinanceCategory = (id, data) => api.patch(`/api/finance/categories/${id}`, data);
export const getFinanceDocuments = (params) => api.get('/api/finance/documents', { params });
export const getFinanceDocument = (id) => api.get(`/api/finance/documents/${id}`);
export const getFinanceDuplicates = (params) => api.get('/api/finance/documents/duplicates', { params });
export const createFinanceDocument = (data) => api.post('/api/finance/documents', data);
export const updateFinanceDocument = (id, data) => api.patch(`/api/finance/documents/${id}`, data);
export const archiveFinanceDocument = (id) => api.post(`/api/finance/documents/${id}/archive`);
export const addFinanceRelation = (id, documentId) => api.post(`/api/finance/documents/${id}/relations`, { documentId });
export const removeFinanceRelation = (id, otherId) => api.delete(`/api/finance/documents/${id}/relations/${otherId}`);
export const addFinanceLineLink = (data) => api.post('/api/finance/line-links', data);
export const addFinanceReceipt = (id, data) => api.post(`/api/finance/documents/${id}/receipts`, data);
export const setFinancePayment = (id, data) => api.post(`/api/finance/documents/${id}/payment`, data);
export const linkFinanceCompany = (id, companyId) => api.post(`/api/finance/documents/${id}/company`, { companyId });
export const getFinanceAudit = (id) => api.get(`/api/finance/documents/${id}/audit`);
export const getFinanceFileUploadUrl = (data) => api.post('/api/finance/files/presign', data);
export const addFinanceFile = (id, data) => api.post(`/api/finance/documents/${id}/files`, data);
export const getFinanceFileUrl = (id, fileId) => `${API_URL}/api/finance/documents/${id}/files/${fileId}`;

// Tools endpoints
export const getTools = () => api.get('/api/tools');
export const getTool = (id) => api.get(`/api/tools/${id}`);
export const createTool = (data) => api.post('/api/tools', data);
export const updateTool = (id, data) => api.put(`/api/tools/${id}`, data);
export const deleteTool = (id) => api.delete(`/api/tools/${id}`);

// Tool Assignment endpoints
export const getToolAssignments = (toolId) => api.get(`/api/tools/${toolId}/assignments`);
export const assignTool = (toolId, data) => api.post(`/api/tools/${toolId}/assign`, data);
export const unassignTool = (toolId, assignmentId) => api.delete(`/api/tools/${toolId}/assignments/${assignmentId}`);

// Material Equipment endpoints
export const getMaterialEquipment = ({ sort, order } = {}) => {
  const params = new URLSearchParams();
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  return api.get(`/api/material-equipment${params.toString() ? `?${params.toString()}` : ''}`);
};
export const getMaterialEquipmentItem = (id) => api.get(`/api/material-equipment/${id}`);
export const createMaterialEquipment = (data) => api.post('/api/material-equipment', data);
export const updateMaterialEquipment = (id, data) => api.put(`/api/material-equipment/${id}`, data);
export const getMaterialEquipmentHistory = (id) => api.get(`/api/material-equipment/${id}/history`);
export const getMaterialEquipmentTemplates = () => api.get('/api/material-equipment/templates');
export const createMaterialEquipmentTemplate = (data) => api.post('/api/material-equipment/templates', data);
export const getMaterialEquipmentTemplate = (id) => api.get(`/api/material-equipment/templates/${id}`);
export const updateMaterialEquipmentTemplate = (id, data) => api.put(`/api/material-equipment/templates/${id}`, data);
export const deleteMaterialEquipmentTemplate = (id) => api.delete(`/api/material-equipment/templates/${id}`);
export const getMaterialEquipmentAssignments = (materialEquipmentId) =>
  api.get(`/api/material-equipment/${materialEquipmentId}/assignments`);
export const assignMaterialEquipment = (materialEquipmentId, data) =>
  api.post(`/api/material-equipment/${materialEquipmentId}/assign`, data);
export const unassignMaterialEquipment = (materialEquipmentId, assignmentId) =>
  api.delete(`/api/material-equipment/${materialEquipmentId}/assignments/${assignmentId}`);
export const getMachineTemplateCategories = (templateId) =>
  api.get(`/api/material-equipment-files/templates/${templateId}/categories`);
export const createMachineTemplateCategory = (templateId, data) =>
  api.post(`/api/material-equipment-files/templates/${templateId}/categories`, data);
export const updateMachineTemplateCategory = (categoryId, data) =>
  api.put(`/api/material-equipment-files/categories/${categoryId}`, data);
export const deleteMachineTemplateCategory = (categoryId) =>
  api.delete(`/api/material-equipment-files/categories/${categoryId}`);
export const getMachineFiles = (materialEquipmentId) =>
  api.get(`/api/material-equipment-files/machines/${materialEquipmentId}/files`);
export const getMachineFileUploadUrl = (materialEquipmentId, data) =>
  api.post(`/api/material-equipment-files/machines/${materialEquipmentId}/presign`, data);
export const createMachineFile = (materialEquipmentId, data) =>
  api.post(`/api/material-equipment-files/machines/${materialEquipmentId}/files`, data);
export const updateMachineFile = (fileId, data) =>
  api.patch(`/api/material-equipment-files/files/${fileId}`, data);
export const deleteMachineFile = (fileId) =>
  api.delete(`/api/material-equipment-files/files/${fileId}`);
export const getMaterialMovementAttachmentUploadUrl = (materialEquipmentId, movementId, data) =>
  api.post(`/api/material-equipment/${materialEquipmentId}/movements/${movementId}/presign`, data);
export const createMaterialMovementAttachment = (materialEquipmentId, movementId, data) =>
  api.post(`/api/material-equipment/${materialEquipmentId}/movements/${movementId}/attachment`, data);
// export const deleteMaterialEquipment = (id) => api.delete(`/api/material-equipment/${id}`); // DISABLED - material equipment can only be written off or embedded

// Project Deadline Updates endpoints
export const getProjectDeadlineUpdates = (projectId, { sort, order } = {}) => {
  const params = new URLSearchParams();
  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  return api.get(`/api/project-deadline-updates/project/${projectId}${params.toString() ? `?${params.toString()}` : ''}`);
};
export const getDeadlineUpdate = (id) => api.get(`/api/project-deadline-updates/${id}`);
export const createDeadlineUpdate = (data) => api.post('/api/project-deadline-updates', data);
export const updateDeadlineUpdate = (id, data) => api.put(`/api/project-deadline-updates/${id}`, data);
export const deleteDeadlineUpdate = (id) => api.delete(`/api/project-deadline-updates/${id}`);

// Project Cost Sheets endpoints
export const getProjectCostSheets = (projectId, params = {}) =>
  api.get(`/api/projects/${projectId}/cost-sheets`, { params });
export const getProjectCostSheetDocuments = (projectId) =>
  api.get(`/api/projects/${projectId}/cost-sheet-documents`);
export const getProjectCostSheetDocumentContent = (projectId, documentId) =>
  api.get(`/api/projects/${projectId}/cost-sheet-documents/${documentId}/content`, { responseType: 'blob' });
export const deleteProjectCostSheetDocument = (projectId, documentId) =>
  api.delete(`/api/projects/${projectId}/cost-sheet-documents/${documentId}`);
export const getProjectCostSheetCategories = (projectId, params = {}) =>
  api.get(`/api/projects/${projectId}/cost-sheet-categories`, { params });

export const getProjectGeneralTasks = (projectId) => api.get(`/api/projects/${projectId}/general-tasks`);
export const createProjectGeneralTask = (projectId, data) => api.post(`/api/projects/${projectId}/general-tasks`, data);
export const ensureProjectGeneralTask = (projectId, data) => api.post(`/api/projects/${projectId}/general-tasks/ensure`, data);
export const updateProjectGeneralTask = (projectId, taskId, data) =>
  api.patch(`/api/projects/${projectId}/general-tasks/${taskId}`, data);
export const deleteProjectGeneralTask = (projectId, taskId) =>
  api.delete(`/api/projects/${projectId}/general-tasks/${taskId}`);
export const attachCostSheetsToProjectGeneralTask = (projectId, taskId, costSheetIds) =>
  api.post(`/api/projects/${projectId}/general-tasks/${taskId}/cost-sheets`, { costSheetIds });
export const detachCostSheetFromProjectGeneralTask = (projectId, taskId, costSheetId) =>
  api.delete(`/api/projects/${projectId}/general-tasks/${taskId}/cost-sheets/${costSheetId}`);
export const createCostSheet = (data) => api.post('/api/cost-sheets', data);
export const updateCostSheet = (id, data) => api.put(`/api/cost-sheets/${id}`, data);
export const deleteCostSheet = (id) => api.delete(`/api/cost-sheets/${id}`);
export const uploadCostSheets = (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('projectId', String(projectId));
  return api.post('/api/cost-sheets/upload', formData, {
    transformRequest: [(data, headers) => {
      delete headers['Content-Type'];
      return data;
    }],
  });
};

// Project Labor Entries endpoints
export const getProjectLaborEntries = (projectId) => api.get(`/api/projects/${projectId}/labor-entries`);
export const getProjectRecentLaborEntries = (projectId) => api.get(`/api/projects/${projectId}/labor-entries/recent`);
export const bulkCreateProjectLaborEntries = (projectId, entries) => api.post(`/api/projects/${projectId}/labor-entries/bulk`, Array.isArray(entries) ? entries : { entries });
export const updateProjectLaborEntry = (projectId, entryId, data) => api.put(`/api/projects/${projectId}/labor-entries/${entryId}`, data);
export const deleteProjectLaborEntry = (projectId, entryId) => api.delete(`/api/projects/${projectId}/labor-entries/${entryId}`);

// Project Tools and Materials endpoints
export const getProjectTools = (projectId) => api.get(`/api/projects/${projectId}/tools`);
export const getProjectMaterials = (projectId) => api.get(`/api/projects/${projectId}/materials`);

// Project Personnel endpoints
export const getProjectPersonnel = (projectId) => api.get(`/api/projects/${projectId}/personnel`);
export const getProjectFloorplans = (projectId) => api.get(`/api/projects/${projectId}/floorplans`);
export const createProjectFloorplanObject = (projectId, data) => api.post(`/api/projects/${projectId}/floorplans/objects`, data);
export const updateProjectFloorplanObject = (projectId, objectId, data) => api.put(`/api/projects/${projectId}/floorplans/objects/${objectId}`, data);
export const deleteProjectFloorplanObject = (projectId, objectId) => api.delete(`/api/projects/${projectId}/floorplans/objects/${objectId}`);
export const getProjectFloorplanUploadUrl = (projectId, objectId, data) =>
  api.post(`/api/projects/${projectId}/floorplans/objects/${objectId}/presign`, data);
export const createProjectFloorplanFile = (projectId, objectId, data) =>
  api.post(`/api/projects/${projectId}/floorplans/objects/${objectId}/files`, data);
export const deleteProjectFloorplanFile = (projectId, fileId) =>
  api.delete(`/api/projects/${projectId}/floorplans/files/${fileId}`);
export const getProjectFloorplanFileContent = (projectId, fileId) =>
  api.get(`/api/projects/${projectId}/floorplans/files/${fileId}/content`, { responseType: 'blob' });
export const getProjectOtherDocuments = (projectId) =>
  api.get(`/api/projects/${projectId}/other-documents`);
export const getProjectOtherDocumentUploadUrl = (projectId, data) =>
  api.post(`/api/projects/${projectId}/other-documents/presign`, data);
export const createProjectOtherDocument = (projectId, data) =>
  api.post(`/api/projects/${projectId}/other-documents`, data);
export const updateProjectOtherDocument = (projectId, documentId, data) =>
  api.patch(`/api/projects/${projectId}/other-documents/${documentId}`, data);
export const getProjectOtherDocumentContent = (projectId, documentId, { download } = {}) =>
  api.get(`/api/projects/${projectId}/other-documents/${documentId}/content`, {
    responseType: 'blob',
    params: download ? { download: 1 } : undefined,
  });
export const deleteProjectOtherDocument = (projectId, documentId) =>
  api.delete(`/api/projects/${projectId}/other-documents/${documentId}`);
export const getProjectTaskWorkInputs = (projectId, date) =>
  api.get(`/api/projects/${projectId}/task-work-inputs`, { params: { date } });
export const getProjectTaskWorkInputsByRange = (projectId, from, to) =>
  api.get(`/api/projects/${projectId}/task-work-inputs`, { params: { from, to } });
export const upsertProjectTaskWorkInput = (projectId, taskId, data) =>
  api.put(`/api/projects/${projectId}/task-work-inputs/${taskId}`, data);
export const upsertProjectTaskWorkInputNotes = (projectId, taskId, data) =>
  api.put(`/api/projects/${projectId}/task-work-inputs/${taskId}/notes`, data);
export const deleteProjectTaskWorkInput = (projectId, taskId, date) =>
  api.delete(`/api/projects/${projectId}/task-work-inputs/${taskId}`, { params: { date } });
export const getProjectTaskWorkInputPhotoUploadUrl = (projectId, taskId, data) =>
  api.post(`/api/projects/${projectId}/task-work-inputs/${taskId}/photos/presign`, data);
export const createProjectTaskWorkInputPhoto = (projectId, taskId, data) =>
  api.post(`/api/projects/${projectId}/task-work-inputs/${taskId}/photos`, data);
export const deleteProjectTaskWorkInputPhoto = (projectId, photoId) =>
  api.delete(`/api/projects/${projectId}/task-work-inputs/photos/${photoId}`);

// Daily work cost sheet mapping
export const getDailyWorkCostSheetMappingCalendarStatus = (projectId, { from, to }) =>
  api.get(`/api/projects/${projectId}/daily-work-cost-sheet-mappings`, { params: { from, to } });
export const getDailyWorkCostSheetMappingForDate = (projectId, date) =>
  api.get(`/api/projects/${projectId}/daily-work-cost-sheet-mappings`, { params: { date } });
export const saveDailyWorkCostSheetMappings = (projectId, payload) =>
  api.put(`/api/projects/${projectId}/daily-work-cost-sheet-mappings`, payload);

// Personnel hours endpoints
export const createPersonnelHours = (payload) => api.post('/api/personnel-hours', payload);
export const getPersonnelHoursForProject = (projectId, params = {}) => api.get(`/api/personnel-hours/project/${projectId}`, { params });
export const getProjectRecentPersonnelHours = (projectId) => api.get(`/api/personnel-hours/project/${projectId}/recent`);
export const getPersonnelHoursForPerson = (personnelId, params = {}) => api.get(`/api/personnel-hours/personnel/${personnelId}`, { params });
export const getPersonnelHoursReportByPersonnel = (params) => api.get('/api/personnel-hours/report/by-personnel', { params });
export const getPersonnelHoursReportByProject = (params) => api.get('/api/personnel-hours/report/by-project', { params });
export const getMyPersonnelHours = (params) => api.get('/api/personnel-hours/me', { params });
export const getPersonnelHoursHistory = (id) => api.get(`/api/personnel-hours/${id}/history`);
export const updatePersonnelHours = (id, data) => api.put(`/api/personnel-hours/${id}`, data);
export const deletePersonnelHours = (id) => api.delete(`/api/personnel-hours/${id}`);

// Absences endpoints
export const getAbsencesForPerson = (personnelId) => api.get(`/api/absences/personnel/${personnelId}`);
export const createAbsence = (data) => api.post('/api/absences', data);
export const deleteAbsence = (id) => api.delete(`/api/absences/${id}`);

export const getCostSheetTemplates = () => api.get('/api/cost-sheets/templates');
export const getToolTemplates = () => api.get('/api/tools/templates');

// Tool template CRUD operations
export const createToolTemplate = (data) => api.post('/api/tools/templates', data);
export const getToolTemplate = (id) => api.get(`/api/tools/templates/${id}`);
export const updateToolTemplate = (id, data) => api.put(`/api/tools/templates/${id}`, data);
export const deleteToolTemplate = (id) => api.delete(`/api/tools/templates/${id}`);

// Write-off endpoints
export const getWriteOffs = () => api.get('/api/write-offs');
export const getWriteOffsByType = (itemType) => api.get(`/api/write-offs/by-type/${itemType}`);
export const getWriteOff = (id) => api.get(`/api/write-offs/${id}`);
export const createWriteOff = (data) => api.post('/api/write-offs', data);
export const updateWriteOff = (id, data) => api.put(`/api/write-offs/${id}`, data);
export const deleteWriteOff = (id) => api.delete(`/api/write-offs/${id}`);
export const getAvailableToolsForWriteOff = () => api.get('/api/write-offs/available/tools');
export const getAvailableMaterialEquipmentForWriteOff = () => api.get('/api/write-offs/available/material-equipment');

// Site logistics endpoints
export const getWarehouseInboundUploadUrl = (data) => api.post('/api/warehouse/inbound/presign', data);
export const createWarehouseInbound = (data) => api.post('/api/warehouse/inbound', data);
export const getMerchantDeliveryUploadUrl = (data) => api.post('/api/site/deliveries/merchant/presign', data);
export const createSiteDelivery = (data) => api.post('/api/site/deliveries', data);
export const getSiteStockForProject = (projectId) => api.get(`/api/site/stock/project/${projectId}`);
export const getSiteDeliveriesForProject = (projectId) => api.get(`/api/site/deliveries/project/${projectId}`);
export const deleteSiteDelivery = (id) => api.delete(`/api/site/deliveries/${id}`);
export const createSiteDeparture = (data) => api.post('/api/site/departures', data);
export const getSiteDeparturesForProject = (projectId) => api.get(`/api/site/departures/project/${projectId}`);
export const deleteSiteDeparture = (id) => api.delete(`/api/site/departures/${id}`);

// Work Quantities endpoints
export const getWorkQuantities = (params = {}) => api.get('/api/work-quantities', { params });
export const getWorkQuantitiesForProject = (projectId, fromDate, toDate) => 
  api.get(`/api/work-quantities/project/${projectId}`, { 
    params: { fromDate, toDate } 
  });
export const createWorkQuantity = (data) => api.post('/api/work-quantities', data);
export const updateWorkQuantity = (id, data) => api.put(`/api/work-quantities/${id}`, data);
export const deleteWorkQuantity = (id) => api.delete(`/api/work-quantities/${id}`);
export const bulkCreateWorkQuantities = (data) => api.post('/api/work-quantities/bulk', data);

// Project expenses endpoints
export const getProjectExpensesForProject = (projectId) => api.get(`/api/project-expenses/project/${projectId}`);
export const getProjectExpense = (id) => api.get(`/api/project-expenses/${id}`);
export const createProjectExpense = (data) => api.post('/api/project-expenses', data);
export const updateProjectExpense = (id, data) => api.put(`/api/project-expenses/${id}`, data);
export const deleteProjectExpense = (id) => api.delete(`/api/project-expenses/${id}`);

// Project Timeline endpoints
export const getProjectTimeline = (projectId) => api.get(`/api/project-timelines/project/${projectId}`);
export const createProjectTimeline = (data) => api.post('/api/project-timelines', data);
export const updateProjectTimeline = (id, data) => api.put(`/api/project-timelines/${id}`, data);
export const deleteProjectTimeline = (id) => api.delete(`/api/project-timelines/${id}`);

// Timeline Phase endpoints
export const createTimelinePhases = (timelineId, phases) => api.post(`/api/project-timelines/${timelineId}/phases`, { phases });
export const updateTimelinePhase = (id, data) => api.put(`/api/project-timelines/phases/${id}`, data);
export const deleteTimelinePhase = (id) => api.delete(`/api/project-timelines/phases/${id}`);

// Timeline Phase Group endpoints
export const createTimelinePhaseGroups = (phaseId, groups) => api.post(`/api/project-timelines/phases/${phaseId}/groups`, { groups });
export const updateTimelinePhaseGroup = (id, data) => api.put(`/api/project-timelines/phase-groups/${id}`, data);
export const deleteTimelinePhaseGroup = (id) => api.delete(`/api/project-timelines/phase-groups/${id}`);

// Timeline Floor endpoints
export const createTimelineFloors = (groupId, floors) => api.post(`/api/project-timelines/phase-groups/${groupId}/floors`, { floors });
export const updateTimelineFloor = (id, data) => api.put(`/api/project-timelines/floors/${id}`, data);
export const deleteTimelineFloor = (id) => api.delete(`/api/project-timelines/floors/${id}`);

// Timeline Wall endpoints
export const createTimelineWalls = (floorId, walls) => api.post(`/api/project-timelines/floors/${floorId}/walls`, { walls });
export const updateTimelineWall = (id, data) => api.put(`/api/project-timelines/walls/${id}`, data);
export const deleteTimelineWall = (id) => api.delete(`/api/project-timelines/walls/${id}`);

// Timeline Task endpoints
export const createTimelineTask = (floorId, data) => api.post(`/api/project-timelines/floors/${floorId}/tasks`, data);
export const updateTimelineTask = (id, data) => api.put(`/api/project-timelines/tasks/${id}`, data);
export const deleteTimelineTask = (id) => api.delete(`/api/project-timelines/tasks/${id}`);
export const getTimelineTaskTree = (timelineId) => api.get(`/api/project-timelines/${timelineId}/tasks/tree`);

// Timeline Task-CostSheet endpoints
export const linkTimelineTaskCostSheets = (taskId, costSheetIds) => api.post(`/api/project-timelines/tasks/${taskId}/cost-sheets`, { costSheetIds });
export const updateTimelineTaskCostSheet = (taskId, costSheetId, data) => api.put(`/api/project-timelines/tasks/${taskId}/cost-sheets/${costSheetId}`, data);
export const unlinkTimelineTaskCostSheet = (taskId, costSheetId) => api.delete(`/api/project-timelines/tasks/${taskId}/cost-sheets/${costSheetId}`);

// Wizard progress endpoint
export const saveTimelineWizardProgress = (timelineId, step, data) => api.put(`/api/project-timelines/${timelineId}/wizard-progress`, { step, data });

// General tasks (master + org catalog)
export const getGeneralTasks = (search = '', category = '') =>
  api.get('/api/general-tasks', {
    params: {
      ...(search ? { search } : {}),
      ...(category ? { category } : {}),
    },
  });
export const createGeneralTask = (data) => api.post('/api/general-tasks', data);
export const updateGeneralTask = (id, data) => api.patch(`/api/general-tasks/${id}`, data);
export const deleteGeneralTask = (id) => api.delete(`/api/general-tasks/${id}`);

export const login = (data) => api.post('/api/auth/login', data);
export const getCurrentUser = () => api.get('/api/auth/me');
export const changePassword = (data) => api.post('/api/auth/change-password', data);
export const requestPasswordReset = (data) => api.post('/api/auth/forgot-password', data);
export const resetPassword = (data) => api.post('/api/auth/reset-password', data);
export const acceptInvite = (data) => api.post('/api/auth/invite/accept', data);
export const getOrganisationUsers = () => api.get('/api/organisation/users');
export const getOrganisationSettings = () => api.get('/api/organisation/settings');
export const updateOrganisationSettings = (data) => api.patch('/api/organisation/settings', data);
export const getDailyWorkPreferences = () => api.get('/api/organisation/daily-work-preferences');
export const updateOrganisationUserEmail = (userId, data) => api.patch(`/api/organisation/users/${userId}/email`, data);
export const removeOrganisationUser = (userId) => api.delete(`/api/organisation/users/${userId}`);
export const updateOrganisationUserRole = (userId, data) => api.patch(`/api/organisation/users/${userId}/role`, data);
export const getOrganisationUserProjects = (userId) => api.get(`/api/organisation/users/${userId}/projects`);
export const updateOrganisationUserProjects = (userId, data) => api.put(`/api/organisation/users/${userId}/projects`, data);
export const getPermissionCatalog = () => api.get('/api/organisation/permissions/catalog');
export const getOrganisationRoles = () => api.get('/api/organisation/roles');
export const createOrganisationRole = (data) => api.post('/api/organisation/roles', data);
export const updateOrganisationRole = (id, data) => api.patch(`/api/organisation/roles/${id}`, data);
export const deleteOrganisationRole = (id) => api.delete(`/api/organisation/roles/${id}`);
export const getOrganisationInvites = () => api.get('/api/organisation/invites');
export const getInviteEligiblePersonnel = () => api.get('/api/organisation/invites/eligible-personnel');
export const createOrganisationInvite = (data) => api.post('/api/organisation/invites', data);
export const linkOrganisationUserPersonnel = (userId, data) => api.patch(`/api/organisation/users/${userId}/link-personnel`, data);
export const unlinkOrganisationUserPersonnel = (userId) => api.delete(`/api/organisation/users/${userId}/link-personnel`);
export const deleteOrganisationInvite = (inviteId) => api.delete(`/api/organisation/invites/${inviteId}`);
export const getPlatformOrganisations = () => api.get('/api/platform/organisations');
export const createPlatformOrganisation = (data) => api.post('/api/platform/organisations', data);
export const joinPlatformOrganisation = (id) => api.post(`/api/platform/organisations/${id}/join`);
export const updatePlatformOrganisation = (id, data) => api.patch(`/api/platform/organisations/${id}`, data);

export default api;